import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState } from "react";
import { useAuth, useLanguage } from "@/app";
import { createPayment } from "@/shared";
import { useBillingEmailGate } from "@/features/billing-email";
import { getPackagePriceRub } from "@/shared/api/packages";
import { navigateToPaymentPage, isWebPaymentEnv, } from "@/shared/lib/paymentNavigation";
import { rememberPendingPaymentForAnalytics } from "@/shared/lib/yandexMetrika";
import { Button, Loader } from "@/shared/ui";
import styles from "./BuyPackageCard.module.scss";
export const BuyPackageCard = ({ packageItem, setIsTopUpModalOpen, }) => {
    const { language } = useLanguage();
    const [isCreatingPayment, setIsCreatingPayment] = useState(false);
    const { user, refreshUser } = useAuth();
    const { ensureBillingEmail, billingEmailModal } = useBillingEmailGate();
    const canPay = Boolean(packageItem.is_yookassa_available) ||
        Boolean(packageItem.is_lava_available);
    const handlePayClick = () => {
        const provider = packageItem.is_yookassa_available
            ? "yookassa"
            : packageItem.is_lava_available
                ? "lava"
                : null;
        if (!provider)
            return;
        // Веб + почта УЖЕ есть: открываем вкладку оплаты СИНХРОННО в жесте клика —
        // иначе после await (createPayment) браузер заблокирует попап. Приложение
        // остаётся в своей вкладке, поэтому браузерный «назад» его не ломает.
        //
        // Веб + почты НЕТ: вкладку НЕ открываем сейчас — сперва спросим почту
        // (модалка). Иначе открывалась пустая вкладка, пока юзер вводит почту в
        // исходной вкладке. Вкладку оплаты откроем в жесте сабмита модалки почты
        // (см. useBillingEmailGate → gate.payWindow) — новая вкладка, но уже ПОСЛЕ
        // ввода почты, без преждевременной «пустой вкладки».
        //
        // В мини-аппах вкладку не открываем — оплата уходит во внешний браузер.
        const hasBillingEmail = Boolean(user?.billing_email?.trim());
        const payWindow = isWebPaymentEnv() && hasBillingEmail
            ? window.open("about:blank", "_blank")
            : null;
        void handleCreatePayment(provider, payWindow);
    };
    const handleCreatePayment = async (provider, payWindow) => {
        // Бэк требует billing_email для чека. Если его нет — спросим и сохраним,
        // иначе POST /payments вернёт 400.
        const gate = await ensureBillingEmail();
        if (!gate.ok) {
            payWindow?.close();
            return;
        }
        // Вкладка оплаты: либо открытая синхронно на клике (почта уже была), либо
        // открытая в жесте сабмита модалки почты (почты не было) — берём ту, что
        // есть. Если обе null (мини-апп / попап заблокирован) — фолбэк в текущей.
        const targetWindow = gate.payWindow ?? payWindow;
        setIsCreatingPayment(true);
        try {
            const payment = await createPayment({
                package_id: packageItem.id,
                provider,
                ...(gate.billingEmail ? { billing_email: gate.billingEmail } : {}),
            });
            if (setIsTopUpModalOpen)
                setIsTopUpModalOpen(false);
            rememberPendingPaymentForAnalytics(payment.id);
            navigateToPaymentPage(payment.payment_url, targetWindow);
            // Refresh balance shortly after the payment page opens.
            setTimeout(() => {
                void refreshUser();
            }, 2000);
        }
        catch (error) {
            targetWindow?.close();
            console.error("Ошибка создания платежа:", error);
        }
        finally {
            setIsCreatingPayment(false);
        }
    };
    // Цена приходит строкой («990.00») — нормализуем, иначе `* 2` и вывод врут.
    const priceRub = getPackagePriceRub(packageItem);
    return (_jsxs("div", { className: styles.card, children: [_jsxs("div", { className: styles.head, children: [_jsx("h3", { className: styles.title, children: packageItem.title }), _jsxs("span", { className: styles.oldPrice, children: [priceRub * 2, " \u20BD"] })] }), _jsxs("div", { className: styles.priceBlock, children: [_jsx("span", { className: styles.price, children: priceRub }), _jsx("span", { className: styles.currency, children: "\u20BD" })] }), _jsx(Button, { variant: "primary", size: "md", fullWidth: true, onClick: handlePayClick, disabled: isCreatingPayment || !canPay, leftSection: isCreatingPayment ? _jsx(Loader, { size: "sm" }) : undefined, children: isCreatingPayment
                    ? language === "ru"
                        ? "Открываем…"
                        : "Opening…"
                    : language === "ru"
                        ? "Оплатить"
                        : "Pay" }), billingEmailModal] }));
};
